import type { ProviderCredentials, ProviderAuth } from './ProviderAuth';
import type { ProviderCapabilities } from './ProviderCapabilities';
import type { ProviderConnection } from './ProviderConnection';
import type { ProviderContext } from './ProviderContext';
import { notImplementedError } from './ProviderErrors';
import type { ProviderHealth, ProviderHealthStatus } from './ProviderHealth';
import type { ProviderMetadata } from './ProviderMetadata';
import { unknownRateLimit, type ProviderRateLimit } from './ProviderRateLimit';
import type { ProviderResponse } from './ProviderResponse';
import type { ProviderSync, ProviderSyncRequest } from './ProviderSync';

export interface ProviderSearchRequest { query: string; cursor?: string; limit?: number; }
export interface ProviderResourceRequest { id: string; }
export interface ProviderRecord { id: string; providerId: string; data: Readonly<Record<string, unknown>>; }

export abstract class BaseProvider {
  readonly connection: ProviderConnection = { status: 'disconnected', connectedAt: null, lastCheckedAt: null, message: 'Connector skeleton' };
  readonly healthStatus: ProviderHealthStatus = 'not_implemented';
  readonly authentication: ProviderAuth;
  readonly rateLimit: ProviderRateLimit = unknownRateLimit();
  readonly lastSync: ProviderSync = { status: 'not_implemented', startedAt: null, completedAt: null, cursor: null, recordsProcessed: 0 };

  constructor(public readonly metadata: ProviderMetadata, public readonly capabilities: ProviderCapabilities) {
    this.authentication = { strategy: metadata.authStrategy, status: 'not_configured', expiresAt: null, scopes: [] };
  }

  protected notImplemented<T>(operation: string, context: ProviderContext): Promise<ProviderResponse<T>> {
    return Promise.resolve({ providerId: this.metadata.id, requestId: context.requestId, receivedAt: context.now().toISOString(), result: { ok: false, error: notImplementedError(operation) }, rateLimit: this.rateLimit });
  }

  searchEvents(_request: ProviderSearchRequest, context: ProviderContext) { return this.notImplemented<ProviderRecord[]>('searchEvents', context); }
  searchListings(_request: ProviderSearchRequest, context: ProviderContext) { return this.notImplemented<ProviderRecord[]>('searchListings', context); }
  getListing(_request: ProviderResourceRequest, context: ProviderContext) { return this.notImplemented<ProviderRecord>('getListing', context); }
  getInventory(context: ProviderContext) { return this.notImplemented<ProviderRecord[]>('getInventory', context); }
  getOrders(context: ProviderContext) { return this.notImplemented<ProviderRecord[]>('getOrders', context); }
  getSales(context: ProviderContext) { return this.notImplemented<ProviderRecord[]>('getSales', context); }
  getTransfers(context: ProviderContext) { return this.notImplemented<ProviderRecord[]>('getTransfers', context); }
  health(context: ProviderContext) { return this.notImplemented<ProviderHealth>('health', context); }
  authenticate(_credentials: ProviderCredentials, context: ProviderContext) { return this.notImplemented<ProviderAuth>('authenticate', context); }
  disconnect(context: ProviderContext) { return this.notImplemented<ProviderConnection>('disconnect', context); }
  refresh(context: ProviderContext) { return this.notImplemented<ProviderAuth>('refresh', context); }
  sync(_request: ProviderSyncRequest, context: ProviderContext) { return this.notImplemented<ProviderSync>('sync', context); }
}
